import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DealQuotationLink } from '@/components/sales/DealQuotationLink'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@goodday-clone/ui'
import { BadgeDollarSign, Kanban, Clock, Archive, RefreshCw, Plus, Rocket, ArrowUpRight, CheckCircle2, X, FileText, ExternalLink, ArrowLeft } from 'lucide-react'
import { DealDetailModal, DealDetailData } from '@/components/sales/deal-detail-modal'
import { AddDealModal } from '@/components/sales/add-deal-modal'
import { api } from '@/api/client'
import { useDealStages } from '@/shared/hooks/useCrmVocabulary'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/context/AuthContext'
import { useDealSpaceAccess } from '@/shared/hooks/useDealSpaceAccess'
import { createProjectFromDeal, fetchLinkedProjects, LinkedProjectInfo } from '@/lib/deal-project-utils'
import { groupServicesByCategory } from '@/shared/constants/services'

export interface Deal {
  id: string
  title: string
  client: string
  stage: string
  value: number
  probability: number
  owner?: string | null
  closeDate?: string | null
  notes?: string | null
  organizationId?: string
  createdAt?: string
  updatedAt?: string
  contactPerson?: string
  contactDesignation?: string
  /** The quotation raised for this deal, if any (set by the API). */
  quotation?: { id: string; number: string; status: string } | null
  /** The deal is won, or its advance payment is in, so a delivery project may be launched (set by the API). */
  canLaunchProject?: boolean
  contactEmail?: string
  contactPhone?: string
  source?: string
  number?: string | number
  services?: string[]
  address?: string
  billingAddress?: string
  clientAddress?: string
  gstin?: string
  clientGstin?: string
}

export default function DealsPipelinePage() {
  const { data: dealStages = [] } = useDealStages()
  const [deals, setDeals] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDeal, setSelectedDeal] = useState<DealDetailData | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isAddOpen, setIsAddOpen] = useState(false)

  const navigate = useNavigate()

  const handleCreateQuotation = (dealItem: Deal) => {
    let addr = dealItem.clientAddress || dealItem.address || dealItem.billingAddress || ''
    let gst = dealItem.clientGstin || dealItem.gstin || ''
    if (!addr && dealItem.notes) {
      const am = dealItem.notes.match(/(?:Address|Billing Address|Location):\s*([^,\n\)]+)/i)
      if (am) addr = am[1].trim()
    }
    if (!gst && dealItem.notes) {
      const gm = dealItem.notes.match(/(?:GSTIN|GST|Tax ID):\s*([A-Z0-9]{15})/i)
      if (gm) gst = gm[1].trim()
    }

    const payload = {
      dealId: dealItem.id,
      title: dealItem.title,
      clientName: dealItem.client,
      clientAddress: addr,
      clientGstin: gst,
      contactPerson: dealItem.contactPerson || '',
      contactDesignation: dealItem.contactDesignation || '',
      email: dealItem.contactEmail || '',
      phone: dealItem.contactPhone || '',
      value: dealItem.value || 0,
      notes: dealItem.notes || '',
      description: dealItem.notes || '',
      services: dealItem.services || [],
      owner: dealItem.owner || '',
      closeDate: dealItem.closeDate || '',
      stage: dealItem.stage || 'Closed Won',
    }

    const searchParams = new URLSearchParams({
      new: 'quotation',
      dealId: dealItem.id,
      clientName: dealItem.client || '',
      title: dealItem.title || '',
      contactPerson: dealItem.contactPerson || '',
      contactDesignation: dealItem.contactDesignation || '',
      email: dealItem.contactEmail || '',
      phone: dealItem.contactPhone || '',
      value: String(dealItem.value || 0),
    })

    if (addr) searchParams.set('clientAddress', addr)
    if (gst) searchParams.set('clientGstin', gst)
    if (dealItem.services && dealItem.services.length > 0) {
      searchParams.set('services', dealItem.services.join(','))
    }

    navigate(`/app/sales/quotations?${searchParams.toString()}`, {
      state: { fromDeal: payload },
    })
  }

  const fetchDeals = async () => {
    setLoading(true)
    try {
      const data = await api.get<any>('/deals')
      const dealList = Array.isArray(data) ? data : (data?.deals || data?.data || [])
      const mapped = dealList.map((d: any) => ({
        id: d._id || d.id,
        title: d.title,
        client: d.client,
        stage: d.stage || 'Discovery',
        value: typeof d.value === 'number' ? d.value : parseFloat(d.value) || 0,
        probability: typeof d.probability === 'number' ? d.probability : parseInt(d.probability, 10) || 50,
        owner: d.owner || 'Bob Smith',
        contactPerson: d.contactPerson || '',
        contactDesignation: d.contactDesignation || '',
        quotation: d.quotation || null,
        canLaunchProject: Boolean(d.canLaunchProject),
        contactEmail: d.contactEmail || '',
        contactPhone: d.contactPhone || '',
        closeDate: d.closeDate || 'Q4 2026',
        notes: d.notes || '',
        services: d.services || [],
        address: d.address || d.billingAddress || d.clientAddress || '',
        billingAddress: d.billingAddress || d.address || '',
        clientAddress: d.clientAddress || d.address || '',
        gstin: d.gstin || d.clientGstin || '',
        clientGstin: d.clientGstin || d.gstin || '',
        source: d.source || '',
      }))
      setDeals(mapped)
    } catch (err) {
      console.error('Failed to load pipeline deals:', err)
    } finally {
      setLoading(false)
    }
  }

  const { organization } = useAuth()
  const { canCreateSpace, canViewSpace } = useDealSpaceAccess()
  const [linkedProjects, setLinkedProjects] = useState<Map<string, LinkedProjectInfo>>(new Map())
  const [creatingDealId, setCreatingDealId] = useState<string | null>(null)
  const [notification, setNotification] = useState<string | null>(null)
  const [lastCreatedProjectId, setLastCreatedProjectId] = useState<string | null>(null)

  const refreshLinkedProjects = async () => {
    const map = await fetchLinkedProjects(organization?.id)
    setLinkedProjects(map)
  }

  useEffect(() => {
    fetchDeals()
    refreshLinkedProjects()

    const handleProjCreated = () => refreshLinkedProjects()
    window.addEventListener('projectCreated', handleProjCreated)
    return () => window.removeEventListener('projectCreated', handleProjCreated)
  }, [organization?.id])

  const handleCreateProject = async (deal: Deal) => {
    setCreatingDealId(deal.id)
    try {
      const res = await createProjectFromDeal(deal, organization?.id)
      if (res.success && res.project) {
        setLastCreatedProjectId(res.project.id)
        setNotification(`Space Project "${res.project.name}" [${res.project.key}] created for won deal!`)
        await refreshLinkedProjects()
      } else {
        if (res.error?.includes('already created')) {
          setNotification(res.error)
          await refreshLinkedProjects()
          toast(res.error)
        } else {
          toast.error(res.error || 'Could not create the project.')
        }
      }
    } finally {
      setCreatingDealId(null)
    }
  }

  // Configured stages, so the pipeline can't omit one (this list previously
  // left out 'Closed Lost').
  const stages = dealStages.map((s) => s.name)

  const getStageDeals = (stageName: string) => {
    return deals.filter(
      (d) => d.stage?.toLowerCase() === stageName.toLowerCase()
    )
  }

  const getStageTotal = (stageDeals: Deal[]) => {
    const total = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0)
    return `₹${total.toLocaleString()}`
  }

  const handleCardClick = (deal: Deal) => {
    setSelectedDeal(deal as unknown as DealDetailData)
    setIsDetailOpen(true)
  }

  const handleDealUpdate = (updated: DealDetailData) => {
    setDeals((prev) =>
      prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d))
    )
    setSelectedDeal(updated)
  }

  const handleDealCreated = (newDeal: DealDetailData) => {
    setDeals((prev) => [newDeal as unknown as Deal, ...prev])
  }

  return (
    <div className="space-y-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Deals Pipeline</h1>
            <p className="text-sm text-muted-foreground">Kanban sales stage progression and active pipeline volume</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDeals}
            title="Refresh Pipeline"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setIsAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Deal
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-border pb-3">
        <Link
          to="/app/deals"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <BadgeDollarSign className="h-3.5 w-3.5" /> Active Deals
        </Link>
        <Link
          to="/app/deals/pipeline"
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground shadow-sm flex items-center gap-1.5"
        >
          <Kanban className="h-3.5 w-3.5" /> Pipeline
        </Link>
        <Link
          to="/app/deals/timeline"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <Clock className="h-3.5 w-3.5" /> Timeline
        </Link>
        <Link
          to="/app/deals"
          className="px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1.5 transition-colors"
        >
          <Archive className="h-3.5 w-3.5" /> Archive
        </Link>
      </div>

      {/* Notification Banner */}
      {notification && (
        <div className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 flex items-center justify-between text-sm animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>{notification}</span>
          </div>
          <div className="flex items-center gap-3">
            {lastCreatedProjectId ? (
              <Link to={`/spaces/${lastCreatedProjectId}`} className="font-bold underline hover:no-underline text-xs">
                View Space →
              </Link>
            ) : (
              <Link to="/spaces" className="font-bold underline hover:no-underline text-xs">
                View at /spaces →
              </Link>
            )}
            <button
              onClick={() => {
                setNotification(null)
                setLastCreatedProjectId(null)
              }}
              className="p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900 rounded cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center flex flex-col items-center justify-center">
          <RefreshCw className="h-7 w-7 text-primary animate-spin mb-3" />
          <p className="text-sm text-muted-foreground">Loading pipeline stages from database...</p>
        </div>
      ) : (
        <div className="w-full overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)] min-h-[460px] pb-4 custom-scrollbar rounded-xl">
          <div className="flex gap-4 items-start min-w-max pb-2">
            {stages.map((stage) => {
              const stageDeals = getStageDeals(stage)
              const totalStr = getStageTotal(stageDeals)
              const isWonStage = stage.toLowerCase() === 'closed won' || dealStages.some((s) => s.isWon && s.name.toLowerCase() === stage.toLowerCase())

              return (
                <div
                  key={stage}
                  className={`w-[320px] shrink-0 rounded-xl border p-3.5 flex flex-col max-h-[calc(100vh-250px)] min-h-[300px] shadow-xs ${
                    isWonStage
                      ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-300/60 dark:border-emerald-800/60'
                      : 'bg-muted/20 border-border'
                  }`}
                >
                  <div className="shrink-0 space-y-2 border-b border-border/50 pb-2.5">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-bold uppercase tracking-wider ${isWonStage ? 'text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5' : 'text-foreground'}`}>
                        {isWonStage && <Rocket className="h-3.5 w-3.5" />}
                        {stage}
                      </span>
                      <Badge variant={isWonStage ? 'default' : 'secondary'} className={`text-[10px] ${isWonStage ? 'bg-emerald-600 text-white' : ''}`}>
                        {stageDeals.length}
                      </Badge>
                    </div>
                    <div className={`text-xl font-bold ${isWonStage ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}>
                      {totalStr}
                    </div>
                  </div>
                  <div className="space-y-2.5 overflow-y-auto flex-1 pr-1 custom-scrollbar pt-2.5">
                    {stageDeals.length === 0 ? (
                      <div className="p-4 rounded-xl border border-dashed text-center text-xs text-muted-foreground">
                        No deals in {stage}
                      </div>
                    ) : (
                    stageDeals.map((item) => {
                      const isLinked = linkedProjects.has(item.id)
                      const linkedProj = linkedProjects.get(item.id)

                      return (
                        <div
                          key={item.id}
                          onClick={() => handleCardClick(item)}
                          className={`p-3.5 rounded-xl border text-xs space-y-2 shadow-2xs hover:shadow-sm transition-all cursor-pointer group ${
                            isWonStage
                              ? 'bg-card border-emerald-200 dark:border-emerald-900/60 hover:border-emerald-500'
                              : 'bg-card border-border hover:border-primary/60'
                          }`}
                        >
                          <p className="font-bold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1">{item.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.client}</p>
                          {item.services && item.services.length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-0.5">
                              {groupServicesByCategory(item.services).map(({ category, services: svcs }) => (
                                <span
                                  key={category.category}
                                  className={`text-[9px] px-1.5 py-0.5 rounded font-medium border ${category.badgeClass} truncate max-w-[140px]`}
                                  title={`${category.category}: ${svcs.join(', ')}`}
                                >
                                  <span className="font-bold opacity-80 mr-1">{category.departmentLabel}:</span>
                                  <span>{svcs[0]}{svcs.length > 1 ? ` +${svcs.length - 1}` : ''}</span>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="pt-2 border-t border-border/40 flex items-center justify-between font-semibold text-foreground">
                            <span className="text-muted-foreground font-normal text-[11px]">Owner: {item.owner || 'Unassigned'}</span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-bold">₹{item.value?.toLocaleString()}</span>
                          </div>

                          {(isWonStage || item.stage?.toLowerCase() === 'closed won') ? (
                            <div className="space-y-1.5 mt-2">
                              {item.quotation ? (
                                <DealQuotationLink quotation={item.quotation} className="w-full h-7" iconClassName="h-3 w-3" />
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCreateQuotation(item)
                                  }}
                                  className="w-full border-emerald-500/40 hover:bg-emerald-50 text-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950/40 text-xs font-semibold flex items-center justify-center gap-1.5 shadow-2xs h-7 transition-colors"
                                >
                                  <FileText className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                                  <span>Create Quotation</span>
                                </Button>
                              )}

                              {isLinked ? (
                                canViewSpace && (
                                <Link
                                  to={`/spaces/${linkedProj?.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full py-1.5 px-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60 border border-emerald-300 dark:border-emerald-800 text-[11px] font-semibold flex items-center justify-between transition-colors shadow-2xs"
                                >
                                  <span className="flex items-center gap-1.5 truncate">
                                    <ExternalLink className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                                    <span>View Space</span>
                                  </span>
                                  <span className="text-[10px] font-mono px-1 py-0.2 rounded bg-emerald-100/80 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200 shrink-0">
                                    {linkedProj?.key}
                                  </span>
                                </Link>
                                )
                              ) : (
                                canCreateSpace && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCreateProject(item)
                                  }}
                                  disabled={creatingDealId === item.id}
                                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 shadow-sm h-7"
                                >
                                  {creatingDealId === item.id ? (
                                    <>
                                      <RefreshCw className="h-3 w-3 animate-spin" />
                                      <span>Creating Space...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Rocket className="h-3 w-3" />
                                      <span>Create Project</span>
                                    </>
                                  )}
                                </Button>
                                )
                              )}
                            </div>
                          ) : (
                            <div className="space-y-1.5 mt-2">
                              {item.quotation ? (
                                <DealQuotationLink quotation={item.quotation} className="w-full h-7" iconClassName="h-3 w-3" />
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCreateQuotation(item)
                                  }}
                                  className="w-full border-border/80 hover:bg-muted text-foreground text-xs font-medium flex items-center justify-center gap-1.5 shadow-2xs h-7 transition-colors"
                                >
                                  <FileText className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                                  <span>Create Quotation</span>
                                </Button>
                              )}
                              {!isLinked && canCreateSpace && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCreateProject(item)
                                  }}
                                  disabled={creatingDealId === item.id}
                                  title="The advance payment is in, so delivery can start"
                                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 shadow-sm h-7"
                                >
                                  {creatingDealId === item.id ? (
                                    <>
                                      <RefreshCw className="h-3 w-3 animate-spin" />
                                      <span>Creating Space...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Rocket className="h-3 w-3" />
                                      <span>Create Project</span>
                                    </>
                                  )}
                                </Button>
                              )}
                              {isLinked && canViewSpace && (
                                <Link
                                  to={`/spaces/${linkedProj?.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full py-1 px-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60 border border-emerald-300 dark:border-emerald-800 text-[11px] font-semibold flex items-center justify-between transition-colors shadow-2xs"
                                >
                                  <span className="flex items-center gap-1.5 truncate">
                                    <ExternalLink className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                                    <span>View Space</span>
                                  </span>
                                  <span className="text-[10px] font-mono px-1 py-0.2 rounded bg-emerald-100/80 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200 shrink-0">
                                    {linkedProj?.key}
                                  </span>
                                </Link>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )
          })}
          </div>
        </div>
      )}

      <DealDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        deal={selectedDeal}
        onUpdate={handleDealUpdate}
        onDeleted={() => fetchDeals()}
        currentSectionName="Pipeline"
        currentSectionHref="/app/deals/pipeline"
      />

      <AddDealModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onDealCreated={handleDealCreated}
      />
    </div>
  )
}
